import React, { useState } from "react";
import { usePerformanceDetailsForPreview } from "@/api/hooks/usePerformanceDetailsForPreview";
import { usePastPerformancesForPreview } from "@/api/hooks/usePastPerformancesForPreview";
import { usePastPerformancesStore } from "@/stores/pastPerformancesStore";
import { X } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import MaskExactProfessionalName from "../../utils/MaskExactProfessionalName";

interface ArtistEconomyPerformanceProps {
  hideDetails?: boolean;
  hidePastHeading?: boolean;
  enableDelete?: boolean;
}

const ArtistEconomyPerformance: React.FC<ArtistEconomyPerformanceProps> = ({
  hideDetails = false,
  hidePastHeading = false,
  enableDelete = false,
}) => {
  const {
    performingCount,
    offstageCount,
    durationText,
    openToTravel,
    languages,
  } = usePerformanceDetailsForPreview();
  const { performances } = usePastPerformancesForPreview();
  const deleteItem = usePastPerformancesStore((s) => s.deleteItem);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  return (
    <>
      {!hideDetails && (
        <>
          <h3 className="text-2xl sm:text-4xl font-bold w-fit bg-gradient-to-r from-[#F16B2E] to-[#EC2179] text-transparent bg-clip-text text-center w-screen">
            Performance Details
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 py-10 my-20 border border-zinc-800 rounded-3xl w-screen">
            <div className="flex flex-col items-center justify-between text-center">
              <p className="text-4xl sm:text-5xl font-bold">{performingCount}</p>
              <p className="text-lg sm:text-xl font-normal max-w-40">
                Performing Member{performingCount !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="flex flex-col items-center justify-between text-center">
              <p className="text-4xl sm:text-5xl font-bold">{durationText}</p>
              <p className="text-lg sm:text-xl font-normal max-w-40">
                (mins) Performing Duration
              </p>
            </div>
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-2xl sm:text-5xl font-bold capitalize">{openToTravel}</p>
              <p className="text-sm sm:text-xl font-normal max-w-40">
                Open To Travel
              </p>
            </div>
            <div className="flex flex-col items-center justify-between text-center">
              <p className="text-5xl sm:text-5xl font-bold">{offstageCount}</p>
              <p className="text-lg sm:text-xl font-normal max-w-40">
                Offstage Members
              </p>
            </div>

            <div className="flex flex-col items-center justify-between text-center max-sm:col-span-2">
              <div className="flex flex-wrap justify-between gap-2 text-sm sm:text-base font-bold">
                {languages.map((lang) => (
                  <p
                    key={lang.l_slug}
                    className="uppercase bg-white text-black px-2 py-1 rounded-lg"
                  >
                    {lang.code}
                  </p>
                ))}
              </div>
              <p className="text-lg sm:text-xl font-normal max-w-40 mt-2">
                Performing Languages
              </p>
            </div>
          </div>
        </>
      )}
      <div className="flex justify-center items-center">

      </div>
      <div className=" w-fit">
        {!hidePastHeading && (
          <h3 className="text-2xl sm:text-4xl mb-5 font-bold w-fit bg-gradient-to-r from-[#F16B2E] to-[#EC2179] text-transparent bg-clip-text text-center mx-auto">
            PAST PERFORMANCES
          </h3>
        )}
        {/* Table format for mobile screens */}
        <div className="block sm:hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white">
                <th className="p-2 ">Total Events Performed</th>
                <th className="p-2 ">Primary Event Type</th>
                <th className="p-2 ">Popular Ones</th>
              </tr>
            </thead>
            <tbody>
              {performances.map((item) => {
                const isReal = item.id > 0;
                const canDelete = enableDelete && isReal;
                const isDeleting = deletingId === item.id;

                const handleDelete = async () => {
                  if (!canDelete || deletingId !== null) return;
                  try {
                    setDeletingId(item.id as number);
                    await deleteItem(item.id as number);
                  } finally {
                    setDeletingId(null);
                  }
                };

                return (
                  <tr key={item.id} className="text-center relative">
                    <td className="p-2 ">{item.event_count}</td>
                    <td className="p-2 ">
                      <MaskExactProfessionalName value={item.event_name} />
                    </td>
                    <td className="p-2 relative overflow-visible">
                      {canDelete && (
                        <button
                          type="button"
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="absolute top-1 right-1 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-xs text-white border border-white/40 disabled:opacity-60"
                        >
                          {isDeleting ? <Loader size="small" /> : <X className="h-3 w-3" />}
                        </button>
                      )}
                      <MaskExactProfessionalName value={item.desc || "—"} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Grid format for larger screens */}
        <div className="hidden sm:block space-y-4 p-6 sm:p-10 text-lg">
          <div className="grid grid-cols-5 gap-3 border-b-4 border-gray-600 pb-5 text-center font-semibold">
            <div>Total Events Performed</div>
            <div></div>
            <div>Primary Event Types</div>
            <div></div>
            <div>Popular Ones</div>
          </div>
          {performances.map((item) => {
            const isReal = item.id > 0;
            const canDelete = enableDelete && isReal;
            const isDeleting = deletingId === item.id;

            const handleDelete = async () => {
              if (!canDelete || deletingId !== null) return;
              try {
                setDeletingId(item.id as number);
                await deleteItem(item.id as number);
              } finally {
                setDeletingId(null);
              }
            };

            return (
              <div
                key={item.id}
                className="grid grid-cols-5 gap-3 text-center relative overflow-visible"
              >
                <div>{item.event_count}</div>
                <div>-</div>
                <div>
                  <MaskExactProfessionalName value={item.event_name} />
                </div>
                <div>-</div>
                <div className="truncate sm:whitespace-normal relative overflow-visible">
                  {canDelete && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="absolute top-1 right-1 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-xs text-white border border-white/40 disabled:opacity-60"
                    >
                      {isDeleting ? <Loader size="small" /> : <X className="h-3 w-3" />}
                    </button>
                  )}
                  <MaskExactProfessionalName value={item.desc || "—"} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ArtistEconomyPerformance;
